import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';
import { queryKeys } from '../../query/queryKeys';
import { strapiMapFormQuestionOptionsToQuestions } from './formQuestionOptionMapQuestions';

function formQuestionOptionMapLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  const name = String(row.form_question_name ?? '').trim();
  const title = String(row.title ?? '').trim();
  return [name, title].filter(Boolean).join(' · ') || String(row.documentId ?? '');
}

type Props = {
  jwt: string | null;
  slug: string;
  selectedDocumentIds: string[];
  questionOptions: DynamicEntityRow[];
  questionLoading?: boolean;
  onMapped?: () => void;
};

export function FormQuestionOptionMapQuestionsBar({
  jwt,
  slug,
  selectedDocumentIds,
  questionOptions,
  questionLoading = false,
  onMapped,
}: Props) {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [questionIds, setQuestionIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const selectedQuestions = useMemo(
    () => questionOptions.filter((row) => questionIds.includes(row.documentId)),
    [questionOptions, questionIds],
  );

  const mapMut = useMutation({
    mutationFn: async () => {
      if (!jwt) throw new Error('Sesi tidak valid.');
      return strapiMapFormQuestionOptionsToQuestions(jwt, {
        formQuestionOptionDocumentIds: selectedDocumentIds,
        formQuestionDocumentIds: questionIds,
      });
    },
    onSuccess: async (result) => {
      setError(null);
      setSuccess(
        `${result.updated_count} opsi dipetakan ke ${result.form_question_count} pertanyaan.`,
      );
      setDialogOpen(false);
      setQuestionIds([]);
      onMapped?.();
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.masterEntityListPrefix(jwt, slug) }),
        qc.invalidateQueries({ queryKey: queryKeys.masterEntityMini(jwt, 'form-question-options') }),
        qc.invalidateQueries({ queryKey: queryKeys.masterEntityMini(jwt, 'form-questions') }),
      ]);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Gagal memetakan opsi ke pertanyaan.'),
  });

  if (!selectedDocumentIds.length && !success && !dialogOpen) return null;

  return (
    <Stack spacing={1} sx={{ mb: 2 }} data-testid="form-question-option-map-questions-bar">
      {selectedDocumentIds.length > 0 ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setError(null);
              setSuccess(null);
              setDialogOpen(true);
            }}
            data-testid="form-question-option-map-questions-open"
          >
            Update map option to form question
          </Button>
          <Typography variant="caption" color="text.secondary">
            {selectedDocumentIds.length} baris dipilih
          </Typography>
        </Stack>
      ) : null}
      {success ? (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      ) : null}

      <Dialog
        open={dialogOpen}
        onClose={() => {
          if (mapMut.isPending) return;
          setDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
        data-testid="form-question-option-map-questions-dialog"
      >
        <DialogTitle>Update map option to form question</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {selectedDocumentIds.length} opsi terpilih akan memakai pertanyaan yang dipilih (bisa lebih
              dari satu). Mapping pertanyaan sebelumnya pada opsi itu diganti.
            </Typography>
            <Autocomplete
              multiple
              options={questionOptions}
              value={selectedQuestions}
              loading={questionLoading}
              onChange={(_, rows) => setQuestionIds(rows.map((row) => row.documentId))}
              getOptionLabel={(row) => formQuestionOptionMapLabel(row)}
              isOptionEqualToValue={(a, b) => a.documentId === b.documentId}
              renderInput={(params) => (
                <TextField {...params} label="Form question" size="small" fullWidth />
              )}
            />
            {error ? (
              <Alert severity="error" data-testid="form-question-option-map-questions-error">
                {error}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={mapMut.isPending}>
            Batal
          </Button>
          <Button
            variant="contained"
            disabled={mapMut.isPending || selectedDocumentIds.length === 0 || questionIds.length === 0}
            onClick={() => mapMut.mutate()}
            data-testid="form-question-option-map-questions-submit"
          >
            {mapMut.isPending ? 'Menyimpan…' : 'Update map'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
